import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { AddCircleOutline as AddIcon } from "@mui/icons-material";
import {
  ChildQuestionConfigDetailDto,
  QuestionComponentType,
} from "@green0meter/api-client";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import QuestionTypeSelect from "../../../QuestionTypeSelect";
import QuestionTypeEditor from "../../QuestionTypeEditor";

import { generateId } from "@helpers";
import { Box, Button, Grid, Stack, Card, IconButton } from "@components/base";
import {
  emptyQuestionConfigTranslations,
  useQuestionEditorStore,
} from "@components/main/questions/QuestionEditor";

interface MultiInput {}

const MultiInput: FC<MultiInput> = () => {
  const { newQuestionConfig, setNewQuestionConfig } = useQuestionEditorStore();
  const { t } = useTranslation();

  const addInput = (): void => {
    if (newQuestionConfig) {
      setNewQuestionConfig({
        ...newQuestionConfig,
        childQuestions: [
          ...(newQuestionConfig.childQuestions || []),
          {
            id: generateId(),
            translations: emptyQuestionConfigTranslations,
            code: "",
            componentType: 0,
            dataType: 0,
          },
        ],
      });
    }
  };

  const updateChildQuestion = (
    childQuestion: ChildQuestionConfigDetailDto,
  ): void => {
    if (newQuestionConfig) {
      setNewQuestionConfig({
        ...newQuestionConfig,
        childQuestions: newQuestionConfig.childQuestions?.map((ch) => {
          if (ch.id === childQuestion.id) {
            return childQuestion;
          }

          return ch;
        }),
      });
    }
  };

  const removeChildQuestion = (
    childQuestion: ChildQuestionConfigDetailDto,
  ): void => {
    if (newQuestionConfig) {
      setNewQuestionConfig({
        ...newQuestionConfig,
        childQuestions: newQuestionConfig.childQuestions?.filter(
          (ch) => ch.id !== childQuestion.id,
        ),
      });
    }
  };

  return newQuestionConfig ? (
    <Stack spacing={2}>
      {newQuestionConfig.childQuestions?.map((childQuestion) => (
        <Card key={childQuestion.id}>
          <Box p={2}>
            <Grid container spacing={2}>
              <Grid>
                <DragIndicatorIcon />
              </Grid>
              <Grid flex={1}>
                <Stack spacing={2}>
                  <QuestionTypeSelect
                    questionConfig={childQuestion}
                    setQuestionConfig={updateChildQuestion}
                    filter={[
                      QuestionComponentType.Number,
                      QuestionComponentType.ShortText,
                      QuestionComponentType.DatePicker,
                      QuestionComponentType.Email,
                      QuestionComponentType.Select,
                    ]}
                  />
                  <Box flexGrow={1}>
                    <QuestionTypeEditor
                      questionConfig={childQuestion}
                      setQuestionConfig={(childQuestion) => {
                        updateChildQuestion(childQuestion);
                      }}
                    />
                  </Box>
                </Stack>
              </Grid>
              {!newQuestionConfig.id && (
                <Grid>
                  <IconButton onClick={() => removeChildQuestion(childQuestion)}>
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </Box>
        </Card>
      ))}
      {!newQuestionConfig.id && (
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addInput}>
          {t("questionEditor.addInput")}
        </Button>
      )}
    </Stack>
  ) : null;
};

export default MultiInput;
